// config.ts

function getRequiredEnv(name: string): string {
  const value = import.meta.env[name] as string | undefined;

  if (!value || value.trim() === '') {
    throw new Error(`Missing required environment variable: ${name}`);
  }

  return value.trim();
}

function getOptionalEnv(name: string, fallback = ''): string {
  const value = import.meta.env[name] as string | undefined;
  return value?.trim() || fallback;
}

// -----------------------------------------------------------------------------
// Core CareAmy / FHIR configuration
// -----------------------------------------------------------------------------

export const IP_ADDRESS =  '';

export const projectId = getRequiredEnv('VITE_PROJECT_ID');

export const BASE_URL = getRequiredEnv('VITE_API_URL');

export const CLIENT_ID = getRequiredEnv('VITE_CLIENT_ID');

export const AI_BASE_PATH = getRequiredEnv('VITE_AI_BASE_PATH');

export const VIDEO_SERVICE_URL = getOptionalEnv(
  'VITE_VIDEO_SERVICE_URL',
  AI_BASE_PATH
);

export const AUTH_SERVICE_URL = getOptionalEnv(
  'VITE_AUTH_SERVICE_URL',
  AI_BASE_PATH
);

export const FAQ_API_BASE_URL = getOptionalEnv(
  'VITE_FAQ_API_BASE_URL',
  AI_BASE_PATH
);

export const FAQ_API_KEY = getOptionalEnv('VITE_FAQ_API_KEY');

// Do not put private/internal server keys in VITE_* variables.
// Any VITE_* value is included in the browser JavaScript bundle.
export const AUTH_INTERNAL_KEY = getOptionalEnv('VITE_AUTH_INTERNAL_KEY');

// -----------------------------------------------------------------------------
// Realtime configuration
// -----------------------------------------------------------------------------

export type RealtimeSubscriptionResource =
  | 'Appointment'
  | 'Communication'
  | 'Patient'
  | 'QuestionnaireResponse'
  | 'DocumentReference'
  | 'Composition'
  | 'Task'
  | 'Observation';

type ResourceRealtimeSetting = {
  subscriptionEnabled: boolean;
  pollingIntervalMs: number;
};

/**
 * Central configuration for all FHIR realtime behavior.
 *
 * Set `subscriptionsEnabled` to false to disable every WebSocket subscription
 * without changing individual resource settings. Polling remains available.
 */
export const REALTIME_SUBSCRIPTION_CONFIG = {
  subscriptionsEnabled: true,
  pollingEnabled: true,
  maxTotalSubscriptions: 30,

  resources: {
    Appointment: {
      subscriptionEnabled: false,
      pollingIntervalMs: 60_000,
    },

    Communication: {
      subscriptionEnabled: true,
      pollingIntervalMs: 15_000,
    },

    Patient: {
      subscriptionEnabled: true,
      pollingIntervalMs: 45_000,
    },

    QuestionnaireResponse: {
      subscriptionEnabled: true,
      pollingIntervalMs: 100_000,
    },

    DocumentReference: {
      subscriptionEnabled: false,
      pollingIntervalMs: 60_000,
    },

    Composition: {
      subscriptionEnabled: false,
      pollingIntervalMs: 15_000,
    },

    Task: {
      subscriptionEnabled: false,
      pollingIntervalMs: 90_000,
    },

    Observation: {
      subscriptionEnabled: false,
      pollingIntervalMs: 30_000,
    },
  } satisfies Record<
    RealtimeSubscriptionResource,
    ResourceRealtimeSetting
  >,
} as const;

export function isRealtimeSubscriptionEnabled(
  resourceType: RealtimeSubscriptionResource
): boolean {
  const resourceConfig =
    REALTIME_SUBSCRIPTION_CONFIG.resources[resourceType];

  return (
    REALTIME_SUBSCRIPTION_CONFIG.subscriptionsEnabled &&
    REALTIME_SUBSCRIPTION_CONFIG.maxTotalSubscriptions > 0 &&
    resourceConfig.subscriptionEnabled
  );
}

export function isRealtimePollingEnabled(): boolean {
  return REALTIME_SUBSCRIPTION_CONFIG.pollingEnabled;
}

export function getRealtimePollingInterval(
  resourceType: RealtimeSubscriptionResource
): number {
  return REALTIME_SUBSCRIPTION_CONFIG.resources[resourceType]
    .pollingIntervalMs;
}

export function configuredSubscriptionCriteria<
  T extends RealtimeSubscriptionResource,
>(
  resourceType: T,
  criteria?: string
): string | undefined {
  if (!criteria) {
    return undefined;
  }

  return isRealtimeSubscriptionEnabled(resourceType)
    ? criteria
    : undefined;
}
